import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SoarQueryKeys } from '../queryKeys';
import { matchesEntityScope } from '../routePaths';
import { AutomationRuleDto, automationRuleService } from '../services/automationRuleService';
import { AutomationRule } from '../types';

const parseConditionFilter = (
  filterValues: string[]
): { logicalOp: 'and' | 'or'; operands: { operator: string; value: string }[] } | null => {
  if (filterValues.length !== 1) return null;
  try {
    const parsed = JSON.parse(decodeURIComponent(filterValues[0]));
    if (typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    if (parsed?.and) return { logicalOp: 'and', operands: parsed.and };
    if (parsed?.or) return { logicalOp: 'or', operands: parsed.or };
    return null;
  } catch {
    return null;
  }
};

const applyStringOperator = (fieldValue: string, operator: string, condValue: string): boolean => {
  const f = (fieldValue ?? '').toLowerCase();
  const v = (condValue ?? '').toLowerCase();
  switch (operator) {
    case 'contains':
      return f.includes(v);
    case 'notContains':
      return !f.includes(v);
    case 'starts':
      return f.startsWith(v);
    case 'ends':
      return f.endsWith(v);
    case 'equals':
      return f === v;
    case 'notEqual':
      return f !== v;
    case 'empty':
      return !fieldValue;
    case 'notEmpty':
      return !!fieldValue;
    default:
      return true;
  }
};

const matchesStringFilter = (filterValues: string[], fieldValue: string): boolean => {
  const condition = parseConditionFilter(filterValues);
  if (condition) {
    const results = condition.operands.map(({ operator, value }) => applyStringOperator(fieldValue, operator, value));
    return condition.logicalOp === 'and' ? results.every(Boolean) : results.some(Boolean);
  }
  return filterValues.includes(fieldValue);
};

export interface AutomationRulesState {
  searchTerm: string;
  filters: Record<string, any[]>;
  showRelatedPlaybooks: boolean;
}

export const useAutomationRules = (
  getEntityName?: (id: string) => string,
  getEventName?: (id: string) => string,
  entityScope?: string[]
) => {
  const queryClient = useQueryClient();
  const [state, setState] = useState<AutomationRulesState>({
    searchTerm: '',
    filters: {
      entity: [],
      status: [],
      trigger: [],
    },
    showRelatedPlaybooks: true,
  });

  const mapDtoToRule = useCallback((dto: AutomationRuleDto): AutomationRule => {
    const filters = dto.filter?.filters || undefined;
    const rawFilter = dto.filter;

    return {
      id: dto.ruleId,
      name: dto.name,
      description: dto.description,
      status: dto.active ? 'Active' : 'Inactive',
      lastModified: dto.updatedAt || dto.createdAt || new Date().toISOString(),
      entityType: dto.triggerEntity,
      triggerType: dto.triggerEvent,
      playbookIds: dto.playbooks?.map(p => p.playbookId) || [],
      filters,
      rawFilter,
    };
  }, []);

  const {
    data: allRules = [],
    isLoading,
    refetch: fetchRules,
  } = useQuery({
    queryKey: [SoarQueryKeys.AUTOMATION_RULES],
    queryFn: async () => {
      const data = await automationRuleService.getAllAutomationRules();
      return Array.isArray(data) ? data.map(mapDtoToRule) : [];
    },
  });

  const rules = useMemo(() => allRules.filter(r => matchesEntityScope(r.entityType, entityScope)), [allRules, entityScope]);

  const deleteRuleMutation = useMutation({
    mutationFn: (ruleId: string) => automationRuleService.deleteAutomationRule(ruleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SoarQueryKeys.AUTOMATION_RULES] });
    },
  });

  const unlinkPlaybookMutation = useMutation({
    mutationFn: ({ ruleId, playbookId }: { ruleId: string; playbookId: string }) =>
      automationRuleService.removePlaybookFromRule(ruleId, playbookId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SoarQueryKeys.AUTOMATION_RULES] });
    },
  });

  const setSearchTerm = useCallback((term: string) => {
    setState(prev => ({ ...prev, searchTerm: term }));
  }, []);

  const setFilters = useCallback((filters: React.SetStateAction<Record<string, any[]>>) => {
    setState(prev => ({
      ...prev,
      filters: typeof filters === 'function' ? filters(prev.filters) : filters,
    }));
  }, []);

  const updateFilters = useCallback((field: string, val: unknown) => {
    setState(prev => ({
      ...prev,
      filters: { ...prev.filters, [field]: val as unknown[] },
    }));
  }, []);

  const setFilterEntity = useCallback(
    (entity: string[]) => {
      updateFilters('entity', entity);
    },
    [updateFilters]
  );

  const setFilterStatus = useCallback(
    (status: string[]) => {
      updateFilters('status', status);
    },
    [updateFilters]
  );

  const setFilterTrigger = useCallback(
    (trigger: string[]) => {
      updateFilters('trigger', trigger);
    },
    [updateFilters]
  );

  const toggleShowRelatedPlaybooks = useCallback(() => {
    setState(prev => ({ ...prev, showRelatedPlaybooks: !prev.showRelatedPlaybooks }));
  }, []);

  const updateRuleInList = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [SoarQueryKeys.AUTOMATION_RULES] });
  }, [queryClient]);

  const addRule = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [SoarQueryKeys.AUTOMATION_RULES] });
  }, [queryClient]);

  const filteredRules = useMemo(() => {
    const { searchTerm, filters } = state;
    const { entity: filterEntity = [], status: filterStatus = [], trigger: filterTrigger = [] } = filters;
    const lowerCaseSearchTerm = searchTerm.toLowerCase();

    return rules
      .filter(
        rule =>
          !searchTerm ||
          rule.name.toLowerCase().includes(lowerCaseSearchTerm) ||
          rule.description.toLowerCase().includes(lowerCaseSearchTerm)
      )
      .filter(
        rule =>
          filterEntity.length === 0 || matchesStringFilter(filterEntity, getEntityName ? getEntityName(rule.entityType) : rule.entityType)
      )
      .filter(rule => filterStatus.length === 0 || matchesStringFilter(filterStatus, rule.status))
      .filter(
        rule =>
          filterTrigger.length === 0 || matchesStringFilter(filterTrigger, getEventName ? getEventName(rule.triggerType) : rule.triggerType)
      );
  }, [rules, state, getEntityName, getEventName]);

  const availableEntities = useMemo(() => Array.from(new Set(rules.map(rule => rule.entityType))), [rules]);

  const availableTriggers = useMemo(() => Array.from(new Set(rules.map(rule => rule.triggerType))), [rules]);

  const availableStatuses = useMemo(() => Array.from(new Set(rules.map(rule => rule.status))), [rules]);

  return {
    ...state,
    rules,
    filteredRules,
    availableEntities,
    availableTriggers,
    availableStatuses,
    isLoading,
    isFetched: true, // For backward compatibility if needed, though useQuery has isFetched
    setSearchTerm,
    setFilters,
    updateFilters,
    setFilterEntity,
    setFilterStatus,
    setFilterTrigger,
    toggleShowRelatedPlaybooks,
    updateRuleInList,
    unlinkPlaybook: (ruleId: string, playbookId: string) => unlinkPlaybookMutation.mutate({ ruleId, playbookId }),
    deleteRule: (ruleId: string) => deleteRuleMutation.mutate(ruleId),
    addRule,
    fetchRules,
  };
};
